import { useState, useEffect } from 'react';
import { getAllApprovalSequences, getAllFunds } from '../../../services/firestoreService';
import Table from '../../shared/UI/Table';
import Button from '../../shared/UI/Button';

export default function ApprovalSequenceList({ onAdd, onEdit }) {
  const [sequences, setSequences] = useState([]);
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([getAllApprovalSequences(), getAllFunds()])
      .then(([s, f]) => { setSequences(s); setFunds(f.filter(fn => fn.is_active !== false)); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  function fundName(fid) {
    return funds.find(f => f.id === fid)?.name || fid || '—';
  }

  // Disable Add if every active fund already has a sequence
  const configuredFundIds = new Set(sequences.map(s => s.fund_id));
  const allFundsConfigured = funds.length > 0 && funds.every(f => configuredFundIds.has(f.id));

  const columns = [
    { key: 'fund_id', label: 'Fund', render: row => fundName(row.fund_id) },
    { key: 'name', label: 'Sequence Name' },
    { key: 'stages', label: 'Stages', render: row => (row.stages || []).length },
    { key: 'version', label: 'Version', render: row => `v${row.version || 1}` },
    {
      key: 'actions',
      label: 'Actions',
      render: row => <Button size="sm" variant="secondary" onClick={() => onEdit(row)}>Edit</Button>,
    },
  ];

  if (error) return <div className="alert alert-error">{error}</div>;

  return (
    <div className="card">
      <div className="card-header">
        <h2>Approval Sequences ({sequences.length})</h2>
        <div title={allFundsConfigured ? 'All funds have an approval sequence configured.' : undefined}>
          <Button variant="primary" onClick={onAdd} disabled={allFundsConfigured}>
            + Add Sequence
          </Button>
        </div>
      </div>
      {allFundsConfigured && (
        <div className="alert alert-info" style={{ margin: '12px 16px 0', borderRadius: 'var(--radius)' }}>
          All funds have an approval sequence configured. To make changes, edit an existing sequence.
        </div>
      )}
      <Table
        columns={columns}
        data={sequences}
        loading={loading}
        emptyTitle="No approval sequences configured"
        emptyMessage="Create an approval sequence for each fund."
        emptyAction={{ label: '+ Add Sequence', onClick: onAdd }}
      />
    </div>
  );
}
